import React, { useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { Outlet, useLocation, useNavigate } from "react-router";
import {
  EuiAccordion,
  EuiButton,
  EuiButtonEmpty,
  EuiCodeBlock,
  EuiFlexGroup,
  EuiFlexItem,
  EuiLoadingSpinner,
  EuiPanel,
  EuiSpacer,
  EuiText,
  EuiTitle,
} from "@elastic/eui";
import { useAnketaI18n } from "src/internationalization";
import { useAnketaAuth } from "src/auth/context";

type StatusKind = "notFound" | "error";

/**
 * One clean status page for "not found" and "something broke", rendered INSIDE
 * the app layout so header and side nav stay usable - the way out is always one
 * click away. Big code, one sentence of what happened, the obvious next steps;
 * technical details folded away for support.
 */
export const StatusPage = ({
  kind,
  error,
  eventId,
  onRetry,
}: {
  kind: StatusKind;
  error?: unknown;
  /** Sentry event id of the captured error, quoted for support. */
  eventId?: string;
  onRetry?: () => void;
}): JSX.Element => {
  const { translate } = useAnketaI18n();
  const navigate = useNavigate();
  const location = useLocation();

  const notFound = kind === "notFound";
  const message =
    error instanceof Error ? error.message : error ? String(error) : "";
  const details = [
    message && `${translate("anketa.page.status.detail.message", { defaultMessage: "Message" })}: ${message}`,
    `${translate("anketa.page.status.detail.path", { defaultMessage: "Page" })}: ${location.pathname}${location.search}`,
    eventId && `${translate("anketa.page.status.detail.eventId", { defaultMessage: "Error ID" })}: ${eventId}`,
    `${translate("anketa.page.status.detail.time", { defaultMessage: "Time" })}: ${new Date().toLocaleString("de-DE")}`,
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <EuiFlexGroup
      justifyContent="center"
      alignItems="center"
      style={{ minHeight: "calc(100vh - 160px)", padding: 24 }}
    >
      <EuiFlexItem grow={false} style={{ maxWidth: 560, width: "100%" }}>
        <EuiPanel hasBorder paddingSize="l">
          <EuiText size="s" color={notFound ? "subdued" : "danger"}>
            <p style={{ fontSize: 48, fontWeight: 600, lineHeight: 1, margin: 0 }}>
              {notFound ? "404" : "500"}
            </p>
          </EuiText>
          <EuiSpacer size="m" />
          <EuiTitle size="m">
            <h1>
              {notFound
                ? translate("anketa.page.status.notFound.title", {
                    defaultMessage: "Page not found",
                  })
                : translate("anketa.page.status.error.title", {
                    defaultMessage: "Something went wrong",
                  })}
            </h1>
          </EuiTitle>
          <EuiSpacer size="s" />
          <EuiText size="s" color="subdued">
            <p>
              {notFound
                ? translate("anketa.page.status.notFound.body", {
                    defaultMessage:
                      "This page does not exist, has moved, or you have no access to it.",
                  })
                : translate("anketa.page.status.error.body", {
                    defaultMessage:
                      "This page could not be displayed. The error has been reported - you can reload it or continue elsewhere.",
                  })}
            </p>
          </EuiText>
          {notFound && (
            <EuiText size="xs" color="subdued">
              <p style={{ overflowWrap: "anywhere" }}>
                <code>{location.pathname}</code>
              </p>
            </EuiText>
          )}
          <EuiSpacer size="l" />
          <EuiFlexGroup gutterSize="s" responsive={false} wrap>
            {!notFound && (
              <EuiFlexItem grow={false}>
                <EuiButton
                  fill
                  iconType="refresh"
                  onClick={() => (onRetry ? onRetry() : window.location.reload())}
                >
                  {translate("anketa.page.status.action.retry", {
                    defaultMessage: "Try again",
                  })}
                </EuiButton>
              </EuiFlexItem>
            )}
            <EuiFlexItem grow={false}>
              <EuiButton
                fill={notFound}
                iconType="home"
                onClick={() => navigate("/")}
              >
                {translate("anketa.page.status.action.home", {
                  defaultMessage: "Go to start page",
                })}
              </EuiButton>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiButtonEmpty iconType="arrowLeft" onClick={() => navigate(-1)}>
                {translate("anketa.page.status.action.back", {
                  defaultMessage: "Go back",
                })}
              </EuiButtonEmpty>
            </EuiFlexItem>
            {!notFound && eventId && (
              <EuiFlexItem grow={false}>
                <EuiButtonEmpty
                  iconType="editorComment"
                  onClick={() => Sentry.showReportDialog({ eventId })}
                >
                  {translate("anketa.page.status.action.report", {
                    defaultMessage: "Describe what happened",
                  })}
                </EuiButtonEmpty>
              </EuiFlexItem>
            )}
          </EuiFlexGroup>
          {!notFound && (
            <>
              <EuiSpacer size="l" />
              <EuiAccordion
                id="status-page-details"
                buttonContent={translate("anketa.page.status.details", {
                  defaultMessage: "Technical details",
                })}
                paddingSize="s"
              >
                <EuiCodeBlock
                  language="text"
                  fontSize="s"
                  paddingSize="s"
                  isCopyable
                  whiteSpace="pre-wrap"
                >
                  {details}
                </EuiCodeBlock>
              </EuiAccordion>
            </>
          )}
        </EuiPanel>
      </EuiFlexItem>
    </EuiFlexGroup>
  );
};

/**
 * 404 for unknown routes. Routes appear only once the user (and ideally the
 * live permission matrix) are loaded, so a valid page would briefly be
 * "unknown" on a cold load: wait for the user, and give the matrix a moment,
 * before declaring the page missing.
 */
export const NotFoundPage = (): JSX.Element => {
  const { user, permissionMatrix } = useAnketaAuth();
  const [matrixWaitOver, setMatrixWaitOver] = useState(false);

  useEffect(() => {
    if (permissionMatrix) return;
    const timer = setTimeout(() => setMatrixWaitOver(true), 4000);
    return () => clearTimeout(timer);
  }, [permissionMatrix]);

  if (!user || (!permissionMatrix && !matrixWaitOver)) {
    return (
      <EuiFlexGroup
        justifyContent="center"
        alignItems="center"
        style={{ minHeight: "calc(100vh - 160px)" }}
      >
        <EuiFlexItem grow={false}>
          <EuiLoadingSpinner size="xl" />
        </EuiFlexItem>
      </EuiFlexGroup>
    );
  }
  return <StatusPage kind="notFound" />;
};

/**
 * Catches a crashing page and shows the error page in its place - header and
 * side nav keep working. Reported to Sentry (the id is shown for support).
 * Keyed by the path by the caller, so navigating away clears the error.
 */
export const PageErrorBoundary = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => (
  <Sentry.ErrorBoundary
    fallback={({ error, eventId, resetError }) => (
      <StatusPage kind="error" error={error} eventId={eventId} onRetry={resetError} />
    )}
  >
    {children}
  </Sentry.ErrorBoundary>
);

/** The layouts' <Outlet />, wrapped so a page crash stays inside the layout. */
export const LayoutOutlet = (): JSX.Element => {
  const location = useLocation();
  return (
    <PageErrorBoundary key={location.pathname}>
      <Outlet />
    </PageErrorBoundary>
  );
};
